import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { HeartHandshake, ArrowUpRight, Gift, Shield } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CASHTAG = "$prophetmckuhn1";
const CASHAPP_URL = "https://cash.app/$prophetmckuhn1";

const WAYS = [
  "Support revivals & gatherings",
  "Fund youth mentorship & scholarships",
  "Sustain community outreach",
];

export const Give = () => {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/giving-tiers").then((r) => setTiers(r.data)).catch(() => {});
  }, []);
  return (
  <section
    id="give"
    data-testid="give-section"
    className="relative py-24 sm:py-32 bg-ink text-parchment grain overflow-hidden"
  >
    <div
      className="absolute inset-0 opacity-70"
      style={{
        background:
          "radial-gradient(circle at 20% 20%, rgba(197,160,89,0.16), transparent 42%), radial-gradient(circle at 85% 80%, rgba(197,160,89,0.10), transparent 45%)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>Ways to Donate</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Partner with the ministry.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-parchment/75 leading-relaxed max-w-xl">
              Sow a seed today and believe by faith that
              whatsoever you have prayed shall come to pass, in Christ Jesus —
              Yahshua. Amen. Every seed sown fuels revivals, mentorship, and
              outreach for the next generation.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-3">
              {WAYS.map((w) => (
                <li key={w} className="flex items-center gap-3 text-parchment/85">
                  <HeartHandshake size={18} className="text-gold-light shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2}>
            <button
              data-testid="c501-button"
              onClick={() => setOpen(true)}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-light hover:underline underline-offset-4"
            >
              <Shield size={16} /> View our 501(c)(3) non-profit statement
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="relative rounded-3xl border border-gold/30 bg-white/[0.04] p-8 sm:p-10 backdrop-blur-sm"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#00D632] text-white font-bold text-3xl shadow-lg">
                  $
                </span>
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.2em] text-parchment/50">
                    Cash App
                  </div>
                  <div className="font-serif text-2xl font-semibold">
                    Charles McKuhn Ministries
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-parchment/50">
                  Cashtag
                </div>
                <div
                  data-testid="give-cashtag"
                  className="mt-1 font-serif text-4xl sm:text-5xl font-semibold gold-foil"
                >
                  {CASHTAG}
                </div>
              </div>

              <a
                data-testid="give-cashapp-button"
                href={CASHAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-ink hover:bg-gold-light transition-colors"
              >
                <Gift size={18} />
                Donate via Cash App
                <ArrowUpRight
                  size={18}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>

              <p className="mt-4 text-center text-xs text-parchment/50">
                Opens Cash App · send to {CASHTAG}
              </p>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </div>

      {tiers.length > 0 && (
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Overline>Ways to Partner</Overline>
              <h3 className="mt-3 font-serif text-2xl sm:text-3xl font-semibold">
                Choose how you'll sow.
              </h3>
              <p className="mt-3 text-sm sm:text-base text-parchment/70">
                Every level of partnership carries the same promise — sow as the
                Spirit leads and believe God for the harvest.
              </p>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((t, i) => (
              <Reveal key={t.id} delay={0.1 * i}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                  data-testid={`giving-tier-${t.id}`}
                  className="h-full rounded-3xl border border-gold/25 bg-white/[0.04] p-7 backdrop-blur-sm"
                >
                  <span
                    className="inline-block h-1.5 w-12 rounded-full"
                    style={{ background: t.accent || "#C5A059" }}
                  />
                  <div className="mt-5 font-serif text-2xl font-semibold gold-foil">
                    {t.name}
                  </div>
                  <p className="mt-3 text-sm text-parchment/75 leading-relaxed">
                    {t.description}
                  </p>
                  <a
                    data-testid={`giving-tier-cta-${t.id}`}
                    href={CASHAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold-light hover:underline underline-offset-4"
                  >
                    <Gift size={15} /> Sow a {t.name} seed
                    <ArrowUpRight
                      size={15}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </a>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="c501-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">501(c)(3) Non-Profit Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>Charles McKuhn Ministries is a 501(c)(3) non-profit religious organization recognized under the U.S. Internal Revenue Code. EIN: 43-2446994.</p>
            <p>All gifts, seeds, and love offerings are tax-deductible to the full extent allowed by law. No goods or services are provided in exchange for your contribution.</p>
            <p>Our official IRS determination letter is available upon request. Please contact charlesmckuhn99@gmail.com for giving records or documentation.</p>
            <p className="text-xs">Thank you for sowing into the work of the Kingdom.</p>
          </div>
        </DialogContent>
      </Dialog>
  </section>
  );
};
